import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Database, ArrowRight, CheckCircle, XCircle, Loader2, 
  RefreshCw, Users, Briefcase, Building, AlertTriangle,
  FileText, Sparkles, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HubSpotMigration = () => {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1: Connect, 2: Analyze, 3: Map, 4: Migrate, 5: Complete
  const [loading, setLoading] = useState(false);
  const [hubspotKey, setHubspotKey] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [schema, setSchema] = useState(null);
  const [fieldMappings, setFieldMappings] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [dataPreview, setDataPreview] = useState({
    contacts: 0,
    deals: 0,
    companies: 0
  });

  // Step 1: Test HubSpot Connection
  const handleTestConnection = async () => {
    if (!hubspotKey.trim()) {
      toast.error('Please enter your HubSpot API key');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/hubspot/test-connection?token=${token}`, {
        access_token: hubspotKey
      });

      if (response.data.success) {
        setConnectionStatus('connected');
        setDataPreview(response.data.counts || { contacts: 0, deals: 0, companies: 0 });
        toast.success('Successfully connected to HubSpot!');
        setStep(2);
      } else {
        setConnectionStatus('failed');
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus('failed');
      toast.error(error.response?.data?.detail || 'Failed to connect to HubSpot');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Analyze HubSpot Schema
  const handleAnalyzeSchema = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/hubspot/analyze-schema?token=${token}`, {
        access_token: hubspotKey
      });

      if (response.data.success) {
        setSchema(response.data.schema);
        toast.success('Schema analysis complete!');
        // Automatically generate field mappings
        await handleGenerateMappings(response.data.schema);
      } else {
        toast.error('Failed to analyze schema');
      }
    } catch (error) {
      toast.error('Schema analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Generate AI Field Mappings
  const handleGenerateMappings = async (schemaData) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/hubspot/generate-mappings?token=${token}`, {
        access_token: hubspotKey,
        hubspot_schema: schemaData || schema
      });

      if (response.data.success) {
        setFieldMappings(response.data.mappings);
        toast.success('AI field mappings generated!');
        setStep(3);
      }
    } catch (error) {
      toast.error('Failed to generate field mappings');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Execute Migration
  const handleStartMigration = async () => {
    setLoading(true);
    setStep(4);
    setMigrationProgress(0);

    try {
      // Migrate contacts
      setMigrationStatus('Migrating contacts...');
      setMigrationProgress(20);
      
      const contactsResponse = await axios.post(`${API}/hubspot/migrate?token=${token}`, {
        access_token: hubspotKey,
        object_type: 'contacts',
        field_mappings: fieldMappings?.contacts || {}
      });

      setMigrationProgress(50);
      
      // Migrate deals
      setMigrationStatus('Migrating deals...');
      
      const dealsResponse = await axios.post(`${API}/hubspot/migrate?token=${token}`, {
        access_token: hubspotKey,
        object_type: 'deals',
        field_mappings: fieldMappings?.deals || {}
      });

      setMigrationProgress(80);

      // Generate report
      setMigrationStatus('Generating report...');
      
      const reportResponse = await axios.post(`${API}/hubspot/report?token=${token}`, {
        contacts_result: contactsResponse.data,
        deals_result: dealsResponse.data,
        field_mappings: fieldMappings
      });

      setMigrationProgress(100);
      setReport(reportResponse.data.report);
      setStep(5);
      toast.success('Migration completed successfully!');

    } catch (error) {
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
      setMigrationStatus('Migration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Card data-testid="hubspot-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="text-orange-500" size={24} />
          Connect to HubSpot
        </CardTitle>
        <CardDescription>
          Enter your HubSpot Private App Access Token to begin the migration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-info/10 border border-info/30 rounded-lg p-4">
          <h4 className="font-medium text-info flex items-center gap-2 mb-2">
            <Shield size={16} />
            How to get your HubSpot API Key
          </h4>
          <ol className="text-sm text-slate-600 space-y-1 ml-4 list-decimal">
            <li>Go to your HubSpot account Settings</li>
            <li>Navigate to Integrations → Private Apps</li>
            <li>Create a new private app with these scopes:
              <ul className="ml-4 mt-1 text-xs">
                <li>• crm.objects.contacts.read</li>
                <li>• crm.objects.deals.read</li>
                <li>• crm.objects.companies.read</li>
              </ul>
            </li>
            <li>Copy the Access Token</li>
          </ol>
        </div>

        <div>
          <Label htmlFor="hubspot-key">HubSpot Private App Access Token</Label>
          <Input
            id="hubspot-key"
            type="password"
            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={hubspotKey}
            onChange={(e) => setHubspotKey(e.target.value)}
            className="mt-2 font-mono"
            data-testid="hubspot-key-input"
          />
        </div>

        <div className="flex items-center gap-4">
          <Button
            onClick={handleTestConnection}
            disabled={loading || !hubspotKey}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="test-connection-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} />
                Connecting...
              </>
            ) : (
              <>
                <Database className="mr-2" size={16} />
                Test Connection
              </>
            )}
          </Button>

          {connectionStatus === 'connected' && (
            <Badge className="bg-success text-white">
              <CheckCircle size={14} className="mr-1" />
              Connected
            </Badge>
          )}
          {connectionStatus === 'failed' && (
            <Badge variant="destructive">
              <XCircle size={14} className="mr-1" />
              Connection Failed
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card data-testid="hubspot-analyze-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="text-accent" size={24} />
          Data Analysis
        </CardTitle>
        <CardDescription>
          AI is analyzing your HubSpot data structure
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Users className="mx-auto text-blue-500 mb-2" size={32} />
            <p className="text-2xl font-bold text-blue-600">{dataPreview.contacts}</p>
            <p className="text-sm text-slate-500">Contacts</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <Briefcase className="mx-auto text-green-500 mb-2" size={32} />
            <p className="text-2xl font-bold text-green-600">{dataPreview.deals}</p>
            <p className="text-sm text-slate-500">Deals</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <Building className="mx-auto text-purple-500 mb-2" size={32} />
            <p className="text-2xl font-bold text-purple-600">{dataPreview.companies}</p>
            <p className="text-sm text-slate-500">Companies</p>
          </div>
        </div>

        <Button
          onClick={handleAnalyzeSchema}
          disabled={loading}
          className="w-full bg-accent"
          data-testid="analyze-schema-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={16} />
              Analyzing with AI...
            </>
          ) : (
            <>
              <Sparkles className="mr-2" size={16} />
              Analyze & Generate Field Mappings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card data-testid="hubspot-mapping-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRight className="text-accent" size={24} />
          Field Mappings
        </CardTitle>
        <CardDescription>
          AI has mapped your HubSpot fields to Treppan CRM. Review and proceed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {fieldMappings && (
          <>
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Users size={16} className="text-blue-500" />
                Contact Mappings
              </h4>
              <div className="bg-slate-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                {Object.entries(fieldMappings.contacts || {}).map(([hs, strat]) => (
                  <div key={hs} className="flex items-center justify-between py-1 text-sm">
                    <span className="text-slate-600">{hs}</span>
                    <ArrowRight size={14} className="text-slate-400" />
                    <span className="font-medium text-primary">{strat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Briefcase size={16} className="text-green-500" />
                Deal Mappings
              </h4>
              <div className="bg-slate-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                {Object.entries(fieldMappings.deals || {}).map(([hs, strat]) => (
                  <div key={hs} className="flex items-center justify-between py-1 text-sm">
                    <span className="text-slate-600">{hs}</span>
                    <ArrowRight size={14} className="text-slate-400" />
                    <span className="font-medium text-primary">{strat}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-warning mt-0.5" size={18} />
            <div>
              <p className="font-medium text-warning">Before you proceed</p>
              <p className="text-sm text-slate-600 mt-1">
                This will import your HubSpot data into Treppan CRM. 
                The original data in HubSpot will not be modified.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => setStep(2)}
            disabled={loading}
          >
            Back
          </Button>
          <Button
            onClick={handleStartMigration}
            disabled={loading}
            className="flex-1 bg-success hover:bg-success/90"
            data-testid="start-migration-btn"
          >
            <Database className="mr-2" size={16} />
            Start Migration
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card data-testid="hubspot-progress-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="text-accent animate-spin" size={24} />
          Migration in Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{migrationStatus}</span>
            <span>{migrationProgress}%</span>
          </div>
          <Progress value={migrationProgress} className="h-3" />
        </div>

        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <Loader2 className="mx-auto animate-spin text-accent mb-3" size={48} />
          <p className="text-slate-600">Please wait while we migrate your data...</p>
          <p className="text-xs text-slate-400 mt-2">Do not close this page</p>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep5 = () => (
    <Card data-testid="hubspot-complete-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="text-success" size={24} />
          Migration Complete!
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-success/10 border border-success/30 rounded-lg p-6 text-center">
          <CheckCircle className="mx-auto text-success mb-4" size={64} />
          <h3 className="text-xl font-bold text-success mb-2">
            Your data has been migrated successfully!
          </h3>
          <p className="text-slate-600">
            All your HubSpot contacts and deals are now in Treppan CRM.
          </p>
        </div>

        {report && (
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <FileText size={16} />
              Migration Report
            </h4>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {report.statistics?.contacts?.migrated || 0}
                  </p>
                  <p className="text-sm text-slate-500">Contacts Migrated</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {report.statistics?.deals?.migrated || 0}
                  </p>
                  <p className="text-sm text-slate-500">Deals Migrated</p>
                </div>
              </div>
              {report.summary && (
                <div className="prose prose-sm max-w-none">
                  <p className="text-slate-600 whitespace-pre-wrap">{report.summary}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setStep(1);
              setHubspotKey('');
              setConnectionStatus(null);
              setSchema(null);
              setFieldMappings(null);
              setReport(null);
            }}
          >
            Migrate More Data
          </Button>
          <Button
            onClick={() => window.location.href = '/contacts'}
            className="flex-1 bg-accent"
          >
            View Migrated Contacts
            <ArrowRight className="ml-2" size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="hubspot-migration-page">
      <div>
        <h3 className="text-2xl font-heading font-bold text-primary flex items-center gap-2">
          <Database className="text-orange-500" />
          HubSpot Data Migration
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          AI-powered migration from HubSpot to Treppan CRM
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {[
          { num: 1, label: 'Connect' },
          { num: 2, label: 'Analyze' },
          { num: 3, label: 'Map Fields' },
          { num: 4, label: 'Migrate' },
          { num: 5, label: 'Complete' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              step >= s.num 
                ? 'bg-accent text-white' 
                : 'bg-slate-200 text-slate-500'
            }`}>
              {step > s.num ? <CheckCircle size={16} /> : s.num}
            </div>
            <span className={`ml-2 text-sm ${step >= s.num ? 'text-primary' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {idx < 4 && (
              <div className={`w-12 h-0.5 mx-2 ${step > s.num ? 'bg-accent' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
      {step === 5 && renderStep5()}
    </div>
  );
};

export default HubSpotMigration;
